import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { comma, uncomma, useFormInput } from "@/utils";
import { Confirm, Loading, Toast } from "@/components";
import { ImageUpload } from "@/components/Common/ImageUpload";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import {
    VehicleInfo,
    DriverInfo,
    AdditionalDriverInfo,
    InsuranceTable,
    ReturnInfo,
    BillingInfo,
    InsuranceInfoNormal,
    InsuranceInfoCar,
    InsuranceInfoTotal,
} from "./components";
import { VehicleListPopup, VehicleReturnPopup, BillingSettingPopup, InsuranceConfigPopup } from "./popups";

import { useLazyGetCarByIdQuery, useLazySearchCarByNumberQuery } from "@/redux/carApi";
import { useGetOrderDetailMutation } from "@/redux/orderApi";
import { usePostChargeMutation, usePostPreChargeMutation } from "@/redux/chargeApi";
import { useUploadFileMutation } from "@/redux/uploadApi";
import {
    useDeleteContractGroupMutation,
    useGetContractGroupContractByIdQuery,
    usePostContractGroupMutation,
    usePutContractGroupMutation,
    useReturnContractMutation,
    usePutContractMutation,
    usePostContractMutation,
} from "@/redux/contractApi";
import { useGetTermListQuery } from "@/redux/termApi";
import { useGetInsuranceConfigDetailQuery, useGetInsuranceConfigListQuery } from "@/redux/rentCorpApi";
import { convertUTCToLocal } from "../../utils";

// 초기 계약서 객체
const initialContract = {
    rentCorpId: null,
    carId: null,
    contractCar: {
        carNumber: "",
        cho: "",
        manufacturerName: "",
        modelName: "",
        modelDetailName: "",
        className: "",
        trimName: "",
        bmNo: "",
        boiNo: "",
        boNo: "",
        bsNo: "",
        bdNo: "",
        firstDate: "",
        color: "",
        fuel: "",
        motorType: "",
        displacement: 0,
        mileage: 0,
        gasCurrentAmount: 0,
    },
    insuranceItems: [
        {
            insuranceItemType: "DAEIN_PAY_1", // 대인배상1
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "DAEIN_PAY_2", // 대인배상2
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "DAEMUL_PAY", // 대물배상
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "NO_INS_INJURY", // 무보험차상해
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_DEAD", // 자동차상해(사망)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_INJURY", // 자동차상해(부상)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_BLOCK", // 자동차상해(장해)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "SELF_CAR_DAMAGE", // 자기차량손해
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
            selfCarDamageRangeType: null, // "PRICE" or "RATE" 선택값
            selfCarDamageRateType: null,
        },
        {
            insuranceItemType: "EMERGENCY_SERVICE", // 긴급출동
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
    ],
    contractDrivers: [
        {
            contractDriverType: "MAIN",
            name: "",
            idNumber: "",
            phone: "",
            address: "",
            addressDetail: "",
            driverLicenseType: null,
            driverLicenseNumber: "",
            serialNumber: "",
            issueDate: "",
            licenseVerifyYn: "N",
        },
    ],
    additionalDriverStatus: false,
};

export const ContractEditDetail = () => {
    const { contractGroupId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { contractIndex, contractId } = location.state || {};
    const passedCarId = location.state?.carId;
    const passedOrderId = location.state?.orderId;

    const { formData, handleChange, setFormData } = useFormInput({
        // 공통
        mode: "CREATE", // "CREATE" / "READ" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        servicePopupStatus: false,
        addressPopupStatus: false,
        addressPopupTarget: null, // "MAIN" | "ADDITIONAL" | "ALLOCATION_RELEASE" | "ALLOCATION_RETURN" | "BUSINESS"
        agentPartnersPopupStatus: false,
        salesPartnersPopupStatus: false,
        vehicleListPopupStatus: false,
        vehicleReturnPopupStatus: false,
        vehicleReturnContractIdx: null,
        billingSettingPopupStatus: false,
        termSelectPopupStatus: false,
        orderListPopupStatus: false,
        id: null, // contractGroupId
        businessStatus: false,
        contractType: "",
        memoForClient: "",
        memoForContract: "",
        memoForInsCorp: "",
        contractUploads: [],
        contractTerms: [],
        driverLicenseFileName: "",
        driverLicense2FileName: "",
        bizLicenseFileName: "",
        etcFileName: "",
        addFileName: "",
        addTermFileName: "",
        faxFileUploadId: undefined,
        faxFileUrl: undefined,
        ...initialContract,
    });
    const { mode, toastMsg, confirmStatus, addressPopupStatus, vehicleReturnPopupStatus, billingSettingPopupStatus, isLoading } = formData;
    const [selectedContractIndex, setSelectedContractIndex] = useState(contractIndex || 0); // 선택한 계약서 인덱스
    const [insuranceConfigPopupStatus, setInsuranceConfigPopupStatus] = useState(false);
    const [selectedInsuranceConfigId, setSelectedInsuranceConfigId] = useState(null);

    const [postContractGroup, { data: postData, isLoading: isPostLoading, error: isPostError }] = usePostContractGroupMutation();
    const [putContractGroup, { data: putData, isLoading: isPutLoading, error: isPutError }] = usePutContractGroupMutation();
    const [deleteContractGroup, { isLoading: isDeleteLoading }] = useDeleteContractGroupMutation();
    const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();
    const [doSearchCar, { data: searchCarData, isSearchLoading, isSearchError }] = useLazySearchCarByNumberQuery();
    const [getCarById, { isLoading: carLoading, error: carError }] = useLazyGetCarByIdQuery();
    const [postCharge] = usePostChargeMutation();
    const [postPreCharge] = usePostPreChargeMutation();
    const [getOrderDetail, { data: detailData, isLoading: detailIsLoading, error: detailError }] = useGetOrderDetailMutation();
    const [returnContract, { isLoading: isReturnLoading }] = useReturnContractMutation();
    const {
        data: contractData,
        isLoading: isContractLoading,
        isFetching: isContractFetching,
        error: contractError,
    } = useGetContractGroupContractByIdQuery(
        { contractGroupId, contractId },
        {
            skip: !contractGroupId || !contractId,
        }
    );

    const [putContract] = usePutContractMutation();
    const [postContract, { isLoading: isPostContractLoading }] = usePostContractMutation();

    // 신규 등록 모드 여부
    const isCreateMode = !contractId;

    useEffect(() => {
        console.log(formData);
    }, [formData]);

    // 보험 설정 상세 조회
    const { data: insuranceConfigDetail } = useGetInsuranceConfigDetailQuery(
        {
            rentCorpId: formData?.rentCorpId,
            insuranceConfigId: selectedInsuranceConfigId,
        },
        {
            skip: !formData?.rentCorpId || !selectedInsuranceConfigId,
        }
    );

    // 보험 설정 상세 조회 결과 처리
    useEffect(() => {
        if (insuranceConfigDetail?.data) {
            const processedInsuranceItems = insuranceConfigDetail.data.insuranceConfigItems.map((item) => ({
                id: item.id,
                insuranceItemType: item.insuranceItemType,
                joinYn: item.joinYn,
                limitType: item.limitType,
                selfCarDamageRangeType: item.selfCarDamageRangeType,
                selfCarDamageRateType: item.selfCarDamageRateType,
                compensationRange: item.compensationRange,
                selfPay: item.selfPay,
            }));

            setFormData((prev) => ({
                ...prev,
                insuranceItems: processedInsuranceItems,
            }));
            setInsuranceConfigPopupStatus(false);
        }
    }, [insuranceConfigDetail, setFormData]);

    const handleInsuranceConfigLoad = () => {
        setInsuranceConfigPopupStatus(true);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // 1) 모드 설정 (UPDATE / CREATE)
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (contractGroupId) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: contractGroupId,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                mode: "CREATE",
            }));
        }
    }, [contractGroupId, setFormData]);

    // ─────────────────────────────────────────────────────────────────────────
    // 2) UPDATE 모드일 때: 계약 상세조회 (contractDetailData) 매핑
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (contractData?.data) {
            setFormData((prev) => {
                const updated = {
                    ...prev,
                    ...contractData.data,
                    isLoading: false,
                };

                // allocation의 시간 데이터를 한국 시간대로 변환
                if (updated.allocation) {
                    const allocation = { ...updated.allocation };
                    if (allocation.releasedAt) {
                        allocation.releasedAt = new Date(allocation.releasedAt).toISOString();
                    }
                    if (allocation.returnedAt) {
                        allocation.returnedAt = new Date(allocation.returnedAt).toISOString();
                    }
                    updated.allocation = allocation;
                }

                return updated;
            });

            const response = contractData.data;
            // contractDrivers 변환: idNumber 분리 및 driverLicenseNumber 처리
            const mappedContractDrivers = (response.contractDrivers || []).map((driver) => {
                let updatedDriver = { ...driver };
                // idNumber 분리: 앞 6자리와 뒷 7자리
                if (updatedDriver.idNumber && updatedDriver.idNumber.length >= 7) {
                    updatedDriver.idNumberF = updatedDriver.idNumber.slice(0, 6);
                    updatedDriver.idNumberB = updatedDriver.idNumber.slice(6); // 뒷 7자리 모두 처리
                }
                // driverLicenseNumber 분리: 앞 2자리를 Number 타입의 localCode로 저장하고, 나머지 문자열 재설정
                if (updatedDriver.driverLicenseNumber && updatedDriver.driverLicenseNumber.length >= 2) {
                    updatedDriver.localCode = Number(updatedDriver.driverLicenseNumber.slice(0, 2));
                    updatedDriver.driverLicenseNumber = updatedDriver.driverLicenseNumber.slice(2);
                }
                return updatedDriver;
            });
            // 제2 운전자가 있으면 additionalDriverStatus를 true로 설정
            const additionalDriverExists = mappedContractDrivers.some((driver) => driver.contractDriverType === "ADDITIONAL");
            // contractBusinessInfo가 존재하면 businessStatus를 true로 설정
            const businessStatus = response.contractBusinessInfo ? true : false;

            setFormData((prev) => ({
                ...prev,
                isLoading: false,
                ...response,
                contractDrivers: mappedContractDrivers,
                additionalDriverStatus: additionalDriverExists,
                businessStatus: businessStatus,
                faxFileUploadId: response.charge?.faxFileUploadId || undefined,
                faxFileUrl: response.charge?.faxFileUrl || undefined,
            }));
        }
    }, [contractData]);

    const handleSelectCar = () => {
        setFormData((prev) => ({ ...prev, vehicleListPopupStatus: true }));
    };

    const handleSearchCar = async (carId) => {
        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const res = await getCarById(carId).unwrap();
            const carData = res?.data;
            if (carData) {
                setFormData((prev) => ({
                    ...prev,
                    contractCar: carData,
                    carId: carData.id,
                    isLoading: false,
                    toastMsg: "차량 조회 완료",
                }));
            } else {
                throw new Error("차량 조회 실패");
            }
        } catch (error) {
            console.error("차량 조회 에러:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: "차량 조회 실패",
                isLoading: false,
            }));
        }
    };

    // 전달받은 차량번호가 있으면 자동으로 formData에 세팅하고 handleSearchCar 호출
    useEffect(() => {
        if (passedCarId) {
            setFormData((prev) => ({
                ...prev,
                contractCar: { ...prev.contractCar, id: passedCarId },
            }));
            handleSearchCar(passedCarId);
        }
    }, [passedCarId]);

    useEffect(() => {
        setFormData((prev) => {
            const updated = { ...prev };

            if (prev.additionalDriverStatus) {
                // 추가 운전자가 없으면 추가
                if (!updated.contractDrivers?.some((d) => d.contractDriverType === "ADDITIONAL")) {
                    const mainDriver = updated.contractDrivers?.find((d) => d.contractDriverType === "MAIN") || {
                        contractDriverType: "MAIN",
                        name: "",
                        idNumber: "",
                        phone: "",
                        address: "",
                        addressDetail: "",
                        driverLicenseType: null,
                        driverLicenseNumber: "",
                        serialNumber: "",
                        issueDate: "",
                    };

                    updated.contractDrivers = [
                        mainDriver,
                        {
                            contractDriverType: "ADDITIONAL",
                            name: "",
                            idNumber: "",
                            phone: "",
                            address: "",
                            addressDetail: "",
                            bCode: "",
                            driverLicenseType: null,
                            driverLicenseNumber: "",
                            serialNumber: "",
                            issueDate: "",
                        },
                    ];
                }
            } else {
                // 추가 운전자 제거
                const mainDriver = updated.contractDrivers?.find((d) => d.contractDriverType === "MAIN") || {
                    contractDriverType: "MAIN",
                    name: "",
                    idNumber: "",
                    phone: "",
                    address: "",
                    addressDetail: "",
                    driverLicenseType: null,
                    driverLicenseNumber: "",
                    serialNumber: "",
                    issueDate: "",
                };

                updated.contractDrivers = [mainDriver];
            }

            return updated;
        });
    }, [formData.additionalDriverStatus, setFormData]);

    const buildRequestBody = () => {
        let reqBody = { ...formData };

        // UI용 상태 값들 제거
        delete reqBody.mode;
        delete reqBody.toastMsg;
        delete reqBody.confirmStatus;
        delete reqBody.servicePopupStatus;
        delete reqBody.addressPopupStatus;
        delete reqBody.addressPopupTarget;
        delete reqBody.agentPartnersPopupStatus;
        delete reqBody.salesPartnersPopupStatus;
        delete reqBody.vehicleListPopupStatus;
        delete reqBody.vehicleReturnPopupStatus;
        delete reqBody.vehicleReturnContractIdx;
        delete reqBody.billingSettingPopupStatus;
        delete reqBody.termSelectPopupStatus;
        delete reqBody.orderListPopupStatus;
        delete reqBody.isLoading;

        // accidentCar.displacement가 문자열로 콤마 포함 시 uncomma 처리
        if (reqBody.accidentCar && typeof reqBody.accidentCar.displacement === "string") {
            reqBody.accidentCar.displacement = Number(uncomma(reqBody.accidentCar.displacement));
        }

        // contractCar 필드 처리
        if (reqBody.contractCar) {
            if (typeof reqBody.contractCar.displacement === "string") {
                reqBody.contractCar.displacement = Number(uncomma(reqBody.contractCar.displacement));
            }
            if (typeof reqBody.contractCar.mileage === "string") {
                reqBody.contractCar.mileage = Number(uncomma(reqBody.contractCar.mileage));
            }
        }

        if (Array.isArray(reqBody.contractDrivers)) {
            reqBody.contractDrivers = reqBody.contractDrivers.map((driver) => {
                // 주민등록번호 idNumberF + idNumberB
                if (driver.idNumberF !== undefined && driver.idNumberB !== undefined) {
                    driver.idNumber = driver.idNumberF + driver.idNumberB;
                }
                // 면허번호 localCode + driverLicenseNumber 재설정
                if (driver.localCode && driver.driverLicenseNumber) {
                    driver.driverLicenseNumber = String(driver.localCode) + driver.driverLicenseNumber;
                }
                // driverLicenseType 빈 문자열이면 null로 변환
                return {
                    ...driver,
                    driverLicenseType: driver.driverLicenseType === "" ? null : driver.driverLicenseType,
                };
            });
        }

        // 보험 관련 필드 처리
        if (Array.isArray(reqBody.insuranceItems)) {
            reqBody.insuranceItems = reqBody.insuranceItems.map((item) => ({
                ...item,
                imitType: item.imitType === "" ? null : item.imitType,
                compensationRange: item.compensationRange === "" ? null : item.compensationRange,
                selfPay: item.selfPay === "" ? null : item.selfPay,
                selfCarDamageRangeType: item.selfCarDamageRangeType === "" ? null : item.selfCarDamageRangeType,
                selfCarDamageRateType: item.selfCarDamageRateType === "" ? null : item.selfCarDamageRateType,
            }));
        }

        return reqBody;
    };

    // Validation 체크 함수
    const validateRegister = () => {
        // 필수 필드 검증 함수
        const validateField = (condition, message) => {
            if (condition) {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: message,
                }));
                return false;
            }
            return true;
        };

        // 문자열 필드 검증
        const validateStringField = (value, message) => {
            return validateField(!value || value.trim() === "", message);
        };

        // 주민번호 검증
        const validateIdNumber = (idNumberF, idNumberB, prefix, isRequired = true) => {
            if (isRequired) {
                if (!validateStringField(idNumberF, `${prefix} 주민번호 앞 6자리를 입력해주세요.`)) return false;
                if (!validateField(idNumberF.trim().length !== 6, `${prefix} 주민번호 앞자리는 6자리여야 합니다.`)) return false;
                if (!validateStringField(idNumberB, `${prefix} 주민번호 뒤 7자리를 입력해주세요.`)) return false;
                if (!validateField(idNumberB.trim().length !== 7, `${prefix} 주민번호 뒷자리는 7자리여야 합니다.`)) return false;
            }
            return true;
        };

        // 주소 검증
        const validateAddress = (address, addressDetail, prefix) => {
            if (!validateStringField(address, `${prefix} 주소를 입력해주세요.`)) return false;
            if (!validateStringField(addressDetail, `${prefix} 상세주소를 입력해주세요.`)) return false;
            return true;
        };

        // 운전자 정보 검증
        const validateDriver = (driver, prefix) => {
            if (!driver) return validateField(true, `${prefix} 정보를 입력해주세요.`);
            // if (!validateStringField(driver.name, `${prefix}명을 입력해주세요.`)) return false;
            if (!validateStringField(driver.phone, `${prefix} 연락처를 입력해주세요.`)) return false;
            // if (!validateIdNumber(driver.idNumberF, driver.idNumberB, prefix)) return false;
            // if (!validateAddress(driver.address, driver.addressDetail, prefix)) return false;
            return true;
        };

        // 4. 계약자 정보 검증
        const mainDriver = formData?.contractDrivers?.find((driver) => driver.contractDriverType === "MAIN");
        if (!validateDriver(mainDriver, "계약자")) return false;

        // 제2운전자 정보 검증 (추가 운전자가 있는 경우)
        if (formData?.additionalDriverStatus) {
            const additionalDriver = formData?.contractDrivers?.find((driver) => driver.contractDriverType === "ADDITIONAL");
            if (!validateDriver(additionalDriver, "제2운전자")) return false;
        }

        // 계약차량 번호 필수 체크
        if (!formData.contractCar?.carNumber) {
            setFormData((prev) => ({ ...prev, toastMsg: "대차 차량을 선택해주세요." }));
            return false;
        }

        return true;
    };

    // 실제 계약 생성 함수
    const doCreate = async () => {
        if (!validateRegister()) return;
        setFormData((prev) => ({ ...prev, isLoading: true }));
        try {
            const requestBody = buildRequestBody();
            const res = await postContract({ contractGroupId, body: requestBody }).unwrap();
            setFormData((prev) => ({ ...prev, isLoading: false, toastMsg: "계약이 생성되었습니다." }));
            const newContractId = res?.data?.id;
            if (newContractId) {
                navigate(`/contract/edit/detail/${contractGroupId}`, {
                    state: { contractId: newContractId },
                });
                setTimeout(() => window.location.reload(), 100);
            }
        } catch (error) {
            setFormData((prev) => ({ ...prev, isLoading: false, toastMsg: error?.message || "계약 생성에 실패했습니다." }));
        }
    };

    // 계약 생성(신규 등록) 핸들러: Confirm 팝업 띄우기
    const handleCreate = () => {
        if (!validateRegister()) return;
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "계약서를 생성하시겠습니까?",
                confirmDescription: "",
                confirmButtons: [
                    {
                        label: "예",
                        className: "btn-primary",
                        onClick: doCreate,
                    },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () => setFormData((prev2) => ({ ...prev2, confirmStatus: { confirmPopupStatus: false } })),
                    },
                ],
            },
        }));
    };

    const handleSave = () => {
        if (!validateRegister()) return;

        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "계약서 정보를 수정하시겠습니까?",
                confirmDescription: "",
                confirmButtons: [
                    {
                        label: "예",
                        className: "btn-primary",
                        onClick: async () => {
                            setFormData((prev) => ({
                                ...prev,
                                isLoading: true,
                                confirmStatus: { confirmPopupStatus: false },
                            }));

                            try {
                                const requestBody = buildRequestBody();
                                await putContract({
                                    contractGroupId,
                                    contractId,
                                    body: requestBody,
                                }).unwrap();

                                setFormData((prev) => ({
                                    ...prev,
                                    toastMsg: "계약이 수정되었습니다.",
                                    isLoading: false,
                                }));

                                window.location.reload();
                            } catch (error) {
                                setFormData((prev) => ({
                                    ...prev,
                                    toastMsg: error?.message || "계약 수정에 실패했습니다.",
                                    isLoading: false,
                                }));
                            }
                        },
                    },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev) => ({
                                ...prev,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    const showConfirm = (title, description, buttons) => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: title,
                confirmDescription: description,
                confirmButtons: buttons,
            },
        }));
    };

    // 삭제 확인 핸들러 (API 호출)
    const handleDeleteConfirm = async () => {
        try {
            await deleteContractGroup({
                contractGroupId: formData.id,
            }).unwrap();
            showConfirm("삭제가 완료되었습니다.", "", [
                {
                    label: "확인",
                    className: "btn-primary",
                    onClick: () => navigate("/contract/status"),
                },
            ]);
        } catch (error) {
            console.error("Failed to delete contract:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "계약서 정보 삭제에 실패했습니다.",
            }));
        }
    };

    // 삭제 버튼 클릭 시 확인 모달을 띄우고, "예" 버튼 클릭 시 handleDeleteConfirm 실행
    const handleDelete = () => {
        showConfirm("해당 계약서 정보를 삭제하시겠습니까?", "삭제 후에는 복구할 수 없습니다.", [
            { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
            {
                label: "아니오",
                className: "btn-cancel",
                onClick: () =>
                    setFormData((prev) => ({
                        ...prev,
                        confirmStatus: { confirmPopupStatus: false },
                    })),
            },
        ]);
    };

    // 공통 파일 업로드 핸들러
    const handleCommonFileChange = async (e, uploadType, idx) => {
        const fileObj = e.target.files[0];
        if (!fileObj) return;

        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const formDataObj = new FormData();
            formDataObj.append("file", fileObj);
            const res = await uploadFile(formDataObj).unwrap();
            const newUploadId = res.data?.id || 0;
            setFormData((prev) => {
                // 만약 uploadType이 "ADD_TERM"이면, contractTerms[idx] 업데이트
                if (uploadType === "ADD_TERM") {
                    const updatedTerms = [...(prev.contractTerms || [])];
                    updatedTerms[idx] = {
                        ...updatedTerms[idx],
                        upload: { id: newUploadId },
                        fileName: fileObj.name,
                    };
                    return { ...prev, contractTerms: updatedTerms };
                }
                // 그 외: contractUploads 배열 업데이트
                const updatedUploads = [...(prev.contractUploads || [])];
                const index = updatedUploads.findIndex((x) => x.uploadType === uploadType);
                if (index >= 0) {
                    updatedUploads[index] = {
                        ...updatedUploads[index],
                        upload: { id: newUploadId, fileName: fileObj.name },
                    };
                } else {
                    updatedUploads.push({ uploadType, upload: { id: newUploadId, fileName: fileObj.name } });
                }
                return { ...prev, contractUploads: updatedUploads };
            });
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "파일 업로드 실패",
            }));
        } finally {
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
            }));
        }
    };

    const getTermFileName = (idx) => {
        return formData.contractTerms?.[idx]?.fileName || "";
    };

    const getFileName = (uploadType) => {
        const uploadObj = formData.contractUploads?.find((x) => x.uploadType === uploadType);
        return uploadObj?.upload?.fileName || "";
    };

    const actionReturn = async (contractId, payload) => {
        try {
            await returnContract({
                contractGroupId: formData.id,
                contractId: contractId,
                body: payload,
            }).unwrap();

            setFormData((prev) => ({
                ...prev,
                toastMsg: "반납 등록이 완료되었습니다.",
                confirmStatus: { confirmPopupStatus: false },
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "반납 등록에 실패하였습니다.",
            }));
        }
    };

    // 반납 등록 버튼 이벤트 핸들러
    const handleReturnSubmit = async (contractId) => {
        const allocation = formData.allocation || {};

        // 유효성 검사
        const validationFields = [
            { value: allocation.returnedAt, message: "반납일시를 입력해주세요." },
            // { value: allocation.returnMileage, message: "반납 주행거리를 입력해주세요.", isEmpty: (val) => val == null || val === "" },
            // { value: allocation.returnMethod, message: "반납 방법을 선택해주세요.", isEmpty: (val) => !val || val.trim() === "" },
            // { value: allocation.returnAddress, message: "반납 주소를 입력해주세요.", isEmpty: (val) => !val || val.trim() === "" },
            // {
            //     value: allocation.returnAddressDetail,
            //     message: "반납 주소 상세를 입력해주세요.",
            //     isEmpty: (val) => !val || val.trim() === "",
            // },
            // { value: allocation.returnGasAmount, message: "반납 주유량을 입력해주세요.", isEmpty: (val) => val == null || val === "" },
            { value: allocation.washStatus, message: "세차 상태를 선택해주세요.", isEmpty: (val) => !val || val.trim() === "" },
        ];

        for (const field of validationFields) {
            const isEmpty = field.isEmpty ? field.isEmpty(field.value) : !field.value;
            if (isEmpty) {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: field.message,
                }));
                return;
            }
        }

        // payload 생성: allocation 객체의 필요한 값들만 추출 (uncomma 함수 사용하여 숫자 값 처리)
        const payload = {
            returnedAt: convertUTCToLocal(new Date(allocation.returnedAt)),
            returnMileage: Number(uncomma(allocation.returnMileage)),
            returnMethod: allocation.returnMethod,
            returnAddress: allocation.returnAddress,
            returnAddressDetail: allocation.returnAddressDetail,
            returnGasAmount: allocation.returnGasAmount, // 필요 시 Number(uncomma())로 변환
            washStatus: allocation.washStatus,
        };

        setFormData((prev) => ({
            ...prev,
            isLoading: false,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "해당 계약 차량을 반납처리 하시겠습니까?",
                confirmDescription: "",
                confirmButtons: [
                    {
                        label: "확인",
                        className: "btn-primary",
                        onClick: () => actionReturn(contractId, payload),
                    },
                    {
                        label: "취소",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    // 차량 이미지 업로드 이벤트 핸들러
    const handleImageUploadComplete = ({ uploadId, uploadType, contractId, index, imageType }) => {
        setFormData((prev) => {
            const newCarImages = prev.carImages ? [...prev.carImages] : [];
            newCarImages[index] = {
                upload: { id: uploadId },
                carImageType: imageType,
            };
            return {
                ...prev,
                carImages: newCarImages,
            };
        });
    };

    // 차량 이미지 삭제 이벤트 핸들러
    const handleRemoveCarImage = (contractId, imageIndex) => {
        setFormData((prev) => {
            const newCarImages = prev.carImages ? [...prev.carImages] : [];
            newCarImages.splice(imageIndex, 1);
            return {
                ...prev,
                carImages: newCarImages,
            };
        });
    };

    // 차량 이미지 순서 변경 이벤트 핸들러
    const handleReorderCarImages = (contractId, reorderedImages) => {
        setFormData((prev) => ({
            ...prev,
            carImages: reorderedImages,
        }));
    };

    const handleAdditionalDriverStatusChange = (e) => {
        setFormData((prev) => {
            const updated = { ...prev };
            updated.additionalDriverStatus = e.target.checked;

            if (e.target.checked) {
                // 추가 운전자가 없으면 추가
                if (!updated.contractDrivers?.some((d) => d.contractDriverType === "ADDITIONAL")) {
                    const mainDriver = updated.contractDrivers?.find((d) => d.contractDriverType === "MAIN") || {
                        contractDriverType: "MAIN",
                        name: "",
                        idNumber: "",
                        phone: "",
                        address: "",
                        addressDetail: "",
                        driverLicenseType: null,
                        driverLicenseNumber: "",
                        serialNumber: "",
                        issueDate: "",
                    };

                    updated.contractDrivers = [
                        mainDriver,
                        {
                            contractDriverType: "ADDITIONAL",
                            name: "",
                            idNumber: "",
                            phone: "",
                            address: "",
                            addressDetail: "",
                            bCode: "",
                            driverLicenseType: null,
                            driverLicenseNumber: "",
                            serialNumber: "",
                            issueDate: "",
                        },
                    ];
                }
            } else {
                // 추가 운전자 제거
                const mainDriver = updated.contractDrivers?.find((d) => d.contractDriverType === "MAIN") || {
                    contractDriverType: "MAIN",
                    name: "",
                    idNumber: "",
                    phone: "",
                    address: "",
                    addressDetail: "",
                    driverLicenseType: null,
                    driverLicenseNumber: "",
                    serialNumber: "",
                    issueDate: "",
                };

                updated.contractDrivers = [mainDriver];
            }

            return updated;
        });
    };

    // 운전면허증 파일 업로드 핸들러
    const handleDriverLicenseUpload = async (e, driverType) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const formDataObj = new FormData();
            formDataObj.append("file", file);
            const res = await uploadFile(formDataObj).unwrap();
            const newUploadId = res.data?.id || 0;

            setFormData((prev) => {
                const updated = { ...prev };
                updated.contractDrivers = updated.contractDrivers?.map((driver) => {
                    if (driver.contractDriverType === driverType) {
                        return {
                            ...driver,
                            licenseFileId: newUploadId,
                            licenseFileName: file.name,
                        };
                    }
                    return driver;
                });
                return updated;
            });
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "운전면허증 파일 업로드에 실패했습니다.",
            }));
        }
    };

    // 주소 검색 팝업 핸들러
    const handleAddressSearch = (driverType) => {
        setFormData((prev) => ({
            ...prev,
            addressPopupStatus: true,
            addressPopupTarget: driverType,
        }));
    };

    // Toast 메시지 표시 함수 (상세화면에서는 사용되지 않지만 일관성을 위해 추가)
    const showToast = (message) => {
        setFormData((prev) => ({
            ...prev,
            toastMsg: message,
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">
                                {isCreateMode ? "계약 생성" : mode === "CREATE" ? "계약 등록" : "계약 수정"}{" "}
                                {formData.orderId && <span className="call-contract">CALL</span>}
                            </span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options"></div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            {/* 그룹(계약서 그룹) 영역 – 계약 리스트 */}
                            <div className="form-group">
                                <div className="group-head">
                                    <div className="head-tit">계약 리스트</div>
                                    <div className="head-option"></div>
                                </div>

                                <div className="group-content">
                                    <div className="group-item" style={{ padding: "0px", border: "none" }}>
                                        <div className="group-item-head">
                                            <div className="head-tit">계약-{selectedContractIndex + 1}</div>
                                            <div className="head-option"></div>
                                        </div>
                                        <div className="group-item-body">
                                            <div className="form-item">
                                                <div className="item-head">
                                                    <span className="form-item_tit">
                                                        운전자 정보 <i></i>
                                                    </span>
                                                    <div className="form-item_etc">
                                                        <div className="checkbox">
                                                            <input
                                                                type="checkbox"
                                                                id="additionalDriverStatus"
                                                                name="additionalDriverStatus"
                                                                checked={formData?.additionalDriverStatus}
                                                                onChange={handleAdditionalDriverStatusChange}
                                                            />
                                                            <label htmlFor="additionalDriverStatus">제 2 운전자가 있습니다.</label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="item-body">
                                                    <div className="item-list">
                                                        <DriverInfo
                                                            formData={formData}
                                                            handleChange={handleChange}
                                                            setFormData={setFormData}
                                                            getFileName={getFileName}
                                                            handleCommonFileChange={handleCommonFileChange}
                                                            handleDriverLicenseUpload={handleDriverLicenseUpload}
                                                            handleAddressSearch={handleAddressSearch}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {formData?.additionalDriverStatus && (
                                                <div className="form-item">
                                                    <div className="item-head">
                                                        <span className="form-item_tit">제 2 운전자 정보</span>
                                                        <div className="form-item_etc"></div>
                                                    </div>
                                                    <div className="item-body">
                                                        <div className="item-list">
                                                            <AdditionalDriverInfo
                                                                formData={formData}
                                                                handleChange={handleChange}
                                                                setFormData={setFormData}
                                                                getFileName={getFileName}
                                                                handleCommonFileChange={handleCommonFileChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="form-item">
                                                <div className="item-head">
                                                    <span className="form-item_tit">대여차량 정보</span>
                                                    <div className="form-item_etc"></div>
                                                </div>
                                                <div className="item-body">
                                                    <div className="item-list">
                                                        <VehicleInfo
                                                            formData={formData}
                                                            handleChange={handleChange}
                                                            setFormData={setFormData}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="form-item">
                                                <div className="item-head">
                                                    <span className="form-item_tit">대여차량 이미지</span>
                                                </div>

                                                <ImageUpload
                                                    maxImages={20}
                                                    labelText="이미지"
                                                    inputIdPrefix="car_images"
                                                    placeholderText="사진 업로드"
                                                    onUploadComplete={handleImageUploadComplete}
                                                    uploadType="carImages"
                                                    contractId={formData.id}
                                                    initialImages={formData.carImages || []}
                                                    onRemoveImage={(contractId, imageIndex) => handleRemoveCarImage(contractId, imageIndex)}
                                                    onReorderImages={(contractId, reorderedImages) =>
                                                        handleReorderCarImages(contractId, reorderedImages)
                                                    }
                                                    imageType="RENT_CAR"
                                                />
                                            </div>

                                            <div className="form-item">
                                                <div className="item-head">
                                                    <span className="form-item_tit">배차/반납 정보</span>
                                                    <div className="form-item_etc"></div>
                                                </div>
                                                <div className="item-body">
                                                    <div className="item-list">
                                                        <ReturnInfo
                                                            formData={formData}
                                                            handleChange={handleChange}
                                                            setFormData={setFormData}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {/* 신규 contractInsuranceCondition이 있으면 우선적으로 신규 방식 사용 */}
                                            {formData?.contractInsuranceCondition ? (
                                                /* 새로운 보험 정보 구조가 있는 경우 새로운 3개 컴포넌트 사용 (상세화면이므로 disabled) */
                                                <>
                                                    <div className="form-item">
                                                        <div className="item-head">
                                                            <span className="form-item_tit">보험 정보 (일반 정보)</span>
                                                            <div className="form-item_etc"></div>
                                                        </div>
                                                        <div className="item-body">
                                                            <div className="item-list">
                                                                <InsuranceInfoNormal
                                                                    contractInsuranceCondition={formData?.contractInsuranceCondition}
                                                                    onUpdate={() => {}} // 상세화면이므로 수정 불가
                                                                    contractIndex={0}
                                                                    disabled={true}
                                                                    showToast={showToast}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="form-item">
                                                        <div className="item-head">
                                                            <span className="form-item_tit">보험 정보 (자차 보험)</span>
                                                            <div className="form-item_etc"></div>
                                                        </div>
                                                        <div className="item-body">
                                                            <div className="item-list">
                                                                <InsuranceInfoCar
                                                                    contractInsuranceCondition={formData?.contractInsuranceCondition}
                                                                    onUpdate={() => {}} // 상세화면이므로 수정 불가
                                                                    contractIndex={0}
                                                                    disabled={true}
                                                                    showToast={showToast}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="form-item">
                                                        <div className="item-head">
                                                            <span className="form-item_tit">보험 정보 (종합 보험)</span>
                                                            <div className="form-item_etc"></div>
                                                        </div>
                                                        <div className="item-body">
                                                            <div className="item-list">
                                                                <InsuranceInfoTotal
                                                                    contractInsuranceCondition={formData?.contractInsuranceCondition}
                                                                    onUpdate={() => {}} // 상세화면이므로 수정 불가
                                                                    contractIndex={0}
                                                                    disabled={true}
                                                                    showToast={showToast}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : formData?.insuranceItems && formData.insuranceItems.length > 0 ? (
                                                /* 기존 보험 정보 구조가 있는 경우 기존 방식(InsuranceTable) 사용 */
                                                <div className="form-item">
                                                    <div className="item-head">
                                                        <span className="form-item_tit">보험 정보</span>
                                                        <div className="form-item_etc">
                                                            {/* <button
                                                                type="button"
                                                                className="btn btn-outline"
                                                                onClick={handleInsuranceConfigLoad}
                                                            >
                                                                보험정보 불러오기
                                                            </button> */}
                                                        </div>
                                                    </div>
                                                    <div className="item-body">
                                                        <div className="item-list">
                                                            <InsuranceTable
                                                                insuranceItems={formData.insuranceItems || []}
                                                                onUpdateInsuranceItem={(index, newValues) =>
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        insuranceItems: prev.insuranceItems?.map((item, i) =>
                                                                            i === index ? { ...item, ...newValues } : item
                                                                        ),
                                                                    }))
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-button">
                        <div className="button-row">
                            <button
                                type="button"
                                className="btn btn-xs btn-outline"
                                onClick={() => navigate(`/contract/detail/${contractGroupId}`)}
                            >
                                뒤로가기
                            </button>
                            {isCreateMode ? (
                                <button type="button" className="btn btn-xs btn-primary" onClick={handleCreate}>
                                    계약 생성
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-xs btn-secondary"
                                        onClick={() => {
                                            handleReturnSubmit(formData.id);
                                        }}
                                    >
                                        반납 등록
                                    </button>
                                    <button type="button" className="btn btn-xs btn-primary" onClick={handleSave}>
                                        계약 저장
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                            addressPopupTarget: null,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => {
                            const driverType = prev.addressPopupTarget;
                            const updatedDrivers = prev.contractDrivers?.map((driver) => {
                                if (driver.contractDriverType === driverType) {
                                    return {
                                        ...driver,
                                        address: data.address,
                                        addressDetail: data.addressDetail,
                                        bCode: data.bCode,
                                    };
                                }
                                return driver;
                            });

                            return {
                                ...prev,
                                contractDrivers: updatedDrivers,
                                addressPopupStatus: false,
                                addressPopupTarget: null,
                            };
                        })
                    }
                />
            )}

            {formData.vehicleListPopupStatus && (
                <VehicleListPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, vehicleListPopupStatus: false }))}
                    setFormData={setFormData}
                    rentCorpId={formData?.rentCorpId}
                    onRentCorpSelect={(value) => {
                        setFormData((prev) => ({
                            ...prev,
                            rentCorpId: value,
                        }));
                    }}
                    onVehicleSelect={(vehicleData) => {
                        getCarById(vehicleData.id)
                            .then((res) => {
                                const carData = res.data?.data;
                                setFormData((prev) => ({
                                    ...prev,
                                    contractCar: carData,
                                    carId: carData.id,
                                    insuranceItems: carData?.insurance?.insuranceItems,
                                }));
                            })
                            .catch((error) => {
                                console.error("차량 조회 에러:", error);
                            });
                    }}
                />
            )}

            {vehicleReturnPopupStatus && (
                <VehicleReturnPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, vehicleReturnPopupStatus: false, vehicleReturnContractIdx: null }))}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                />
            )}

            {billingSettingPopupStatus && (
                <BillingSettingPopup
                    layer={true}
                    onClose={() => {
                        setFormData((prev) => ({
                            ...prev,
                            billingSettingPopupStatus: false,
                        }));
                        setSelectedContractIndex(null);
                    }}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    selectedContractIndex={selectedContractIndex}
                />
            )}

            {insuranceConfigPopupStatus && (
                <InsuranceConfigPopup
                    layer={true}
                    onClose={() => setInsuranceConfigPopupStatus(false)}
                    formData={formData}
                    setFormData={setFormData}
                    onInsuranceConfigSelect={(config) => {
                        setSelectedInsuranceConfigId(config.id);
                        setFormData((prev) => ({
                            ...prev,
                            rentCorpId: config.rentCorpId,
                        }));
                    }}
                />
            )}
        </>
    );
};
